import Link from 'next/link'

import { round } from '@framework/utils/lodash'
import { priceFormat } from '@framework/utils/parse-util'

import Spinner from '@components/ui/Spinner'
import {
  GENERAL_MOVE_TO_BAG,
  IMG_PLACEHOLDER,
  WISHLIST_TITLE,
} from '@components/utils/textVariables'
import { handleBrokenImageError, removePrecedingSlash, removeSucceedingSlug } from '@framework/utils/app-util'
import Router from 'next/router'

interface IWishlistSection {
  data: Array<any>
  isLoading: boolean
  handleViewItem: (product: any) => void
  handleAddToCart: (product: any) => void
  handleRemoveFromWishlist: (product: any) => void
}

const WishlistSection = (props: IWishlistSection) => {
  const {
    data,
    isLoading,
    handleViewItem,
    handleAddToCart,
    handleRemoveFromWishlist,
  } = props

  return (
    <>
      <div className="hidden px-4 sm:px-0 sm:block">
        <h1 className="font-bold tracking-tight text-24 text-primary">
          {WISHLIST_TITLE}{' '}
          <span className="ml-1 text-sm text-black opacity-60">
            {data.length} items
          </span>
        </h1>
      </div>
      <section aria-labelledby="recent-heading" className="mt-4">
        {isLoading ? <Spinner /> : null}
        <div className="space-y-16 sm:space-y-24">
          <div className="flow-root px-0 mt-2 sm:mt-4 sm:px-0">
            <div className="grid grid-cols-2 -mx-px sm:gap-y-4 sm:mx-0 md:grid-cols-4 product-listing-main lg:grid-cols-4">
              {data?.map((product: any, wid: number) => {
                const saving =
                  product?.listPrice?.raw?.withTax -
                  product?.price?.raw?.withTax
                const discount = round(
                  (saving / product?.listPrice?.raw?.withTax) * 100,
                  0
                )
                return (
                  <div
                    className="mb-2 origin-center group hover:bg-white"
                    key={wid}
                  >
                    <div
                      key={product?.id}
                      className="relative px-1 pt-0 pb-2 group-hover:pb-0 sm:px-1"
                    >
                      <Link
                        legacyBehavior
                        passHref
                        href={`/${removeSucceedingSlug(product?.slug)}/[slug]`}
                        as={`/${removePrecedingSlash(product?.slug)}`}
                        className="relative overflow-hidden bg-gray-200 radius-xs aspect-w-1 aspect-h-1"
                        onClick={(ev: any) => { 
                          ev.preventDefault()
                          handleViewItem(product)
                          Router.push(`/${removePrecedingSlash(product?.slug)}`)
                        }}
                      >
                        <div className="imae-container">
                          {product?.image != null ? (
                            <>
                              <img
                                placeholder="blur"
                                src={product?.image}
                                alt={product?.name}
                                width={400}
                                height={600}
                                className="object-cover object-center w-full h-full radius-xs sm:h-full"
                                onError={(e: any) => handleBrokenImageError(e)}
                              />
                            </>
                          ) : (
                            <>
                              <img
                                src={IMG_PLACEHOLDER}
                                alt={product?.name}
                                width={400}
                                height={600}
                                className="object-cover object-center w-full h-full radius-xs sm:h-full"
                                onError={(e: any) => handleBrokenImageError(e)}
                              />
                            </>
                          )}
                        </div>
                      </Link>
                      <div className="pt-0">
                        <div className="grid grid-cols-12 px-2 mt-2 sm:grid-cols-12 sm:gap-x-2">
                          <div className="flex items-center col-span-12 sm:col-span-12">
                            <h3 className="text-xs truncate max-width-250 text-brown-light text-10">
                              {product?.classification?.category}
                            </h3>
                          </div>
                        </div>
                        <h3 className="px-2 py-1 font-medium text-12 text-primary min-h-50">
                          <span
                            onClick={() => {
                              Router?.push(`/${product?.slug}`)
                              handleViewItem(product)
                            }}
                            title={product?.name}
                            className="text-truncate-two-lines"
                          >
                            {product?.name}
                          </span>
                        </h3>
                        <p className="px-2 mt-1 mb-2 font-medium text-12 text-primary sm:mt-2 min-h-full sm:mb-0">
                          {priceFormat(product?.price?.raw?.withTax)}
                          {product?.listPrice?.raw?.withTax >
                          product?.price?.raw?.withTax ? (
                            <>
                              <span className="px-2 font-normal text-gray-500 line-through text-12">
                                {priceFormat(product?.listPrice?.raw?.withTax)}
                              </span>
                              <span className="font-normal text-12 text-emerald-500">
                                {discount}% off
                              </span>
                            </>
                          ) : null}
                        </p>

                        <div className="w-full px-2 mt-3">
                          {product?.currentStock > 0 ? (
                            <button
                              onClick={() => handleAddToCart(product)}
                              className="flex items-center justify-center w-full p-3 text-xs font-semibold text-black border"
                            >
                              <span className="mr-2">
                                <i className="sprite-icon sprite-cart" />
                              </span>{' '}
                              {GENERAL_MOVE_TO_BAG}
                            </button>
                          ) : (
                            <button className="flex items-center justify-center w-full px-3 py-4 text-xs font-semibold text-black bg-gray-200 border">
                              Out Of Stock
                            </button>
                          )}
                        </div>
                        <div className="absolute z-10 inline-block top-3 right-1">
                          <button
                            onClick={() => handleRemoveFromWishlist(product)}
                            className="text-red-600 hover:text-red-500"
                          >
                            <span className="mr-2">
                              <i className="sprite-icon sprite-close" />
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default WishlistSection
